import {
  BriefcaseBusiness,
  CalendarOff,
  Mail,
  Pencil,
  Phone,
  Plus,
  Save,
  Search,
  Trash2,
  UserCheck,
  UserMinus,
  UserPlus,
  UsersRound,
} from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";
import { api, newIdempotencyKey } from "../api";
import { useAuth } from "../auth";
import { PageHeader, ResponsiveDetailSurface, StateView, StatusBadge } from "../components";
import { useApiData } from "../hooks";
import {
  brazilianPhoneToE164,
  finalizeIdentifierCode,
  formatBrazilianPhone,
  formatEmailInput,
  formatIdentifierCode,
} from "../input-masks";
import type { Employee, Team } from "../types";

type PeopleTab = "employees" | "teams";
type PeoplePanel =
  | { kind: "employee-form"; employee?: Employee }
  | { kind: "team-form"; team?: Team }
  | { kind: "availability"; employee: Employee }
  | { kind: "members"; team: Team };

const employmentLabels: Record<Employee["employmentType"], string> = {
  employee: "Funcionário",
  contractor: "Terceirizado",
  partner: "Sócio",
};

function displayPhone(value: string) {
  return formatBrazilianPhone(value.startsWith("+55") ? value.slice(3) : value);
}

export function PeoplePage() {
  const { user } = useAuth();
  const employees = useApiData<Employee[]>("/employees?active=true");
  const teams = useApiData<Team[]>("/teams?active=true");
  const [tab, setTab] = useState<PeopleTab>("employees");
  const [panel, setPanel] = useState<PeoplePanel | null>(null);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);

  const [employeeCode, setEmployeeCode] = useState("");
  const [employeeName, setEmployeeName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [employmentType, setEmploymentType] = useState<Employee["employmentType"]>("employee");
  const [technicalRegistration, setTechnicalRegistration] = useState("");

  const [teamCode, setTeamCode] = useState("");
  const [teamName, setTeamName] = useState("");
  const [leaderId, setLeaderId] = useState("");

  const [availabilityKind, setAvailabilityKind] = useState<"unavailable" | "leave" | "break">("unavailable");
  const [availabilityStart, setAvailabilityStart] = useState("");
  const [availabilityEnd, setAvailabilityEnd] = useState("");
  const [availabilityReason, setAvailabilityReason] = useState("");

  const [memberEmployeeId, setMemberEmployeeId] = useState("");

  const metrics = useMemo(() => {
    const people = employees.data ?? [];
    return {
      employees: people.length,
      teams: teams.data?.length ?? 0,
      allocated: people.filter((employee) => employee.teams.length > 0).length,
      unallocated: people.filter((employee) => employee.teams.length === 0).length,
    };
  }, [employees.data, teams.data]);

  const normalizedSearch = search.trim().toLowerCase();
  const filteredEmployees = employees.data?.filter(({ name, code, phone: employeePhone, email: employeeEmail }) =>
    `${name} ${code} ${employeePhone} ${employeeEmail ?? ""}`.toLowerCase().includes(normalizedSearch));
  const filteredTeams = teams.data?.filter(({ name, code, leaderName, members }) =>
    `${name} ${code} ${leaderName ?? ""} ${members.map((member) => member.name).join(" ")}`.toLowerCase().includes(normalizedSearch));

  const closePanel = () => {
    setPanel(null);
    setError("");
    setMemberEmployeeId("");
    setConfirmingRemoval(false);
  };

  const openEmployeeForm = (employee?: Employee) => {
    setError("");
    setConfirmingRemoval(false);
    setEmployeeCode(employee?.code ?? "");
    setEmployeeName(employee?.name ?? "");
    setPhone(employee ? displayPhone(employee.phone) : "");
    setEmail(employee?.email ?? "");
    setEmploymentType(employee?.employmentType ?? "employee");
    setTechnicalRegistration(employee?.technicalRegistration ?? "");
    setPanel({ kind: "employee-form", employee });
  };

  const openTeamForm = (team?: Team) => {
    setError("");
    setConfirmingRemoval(false);
    setTeamCode(team?.code ?? "");
    setTeamName(team?.name ?? "");
    setLeaderId(team?.leaderEmployeeId ?? "");
    setPanel({ kind: "team-form", team });
  };

  const openAvailability = (employee: Employee) => {
    setError("");
    setAvailabilityKind("unavailable");
    setAvailabilityStart("");
    setAvailabilityEnd("");
    setAvailabilityReason("");
    setPanel({ kind: "availability", employee });
  };

  const changeTab = (next: PeopleTab) => {
    setTab(next);
    setSearch("");
    closePanel();
  };

  const submitEmployee = async (event: FormEvent) => {
    event.preventDefault();
    if (!user?.branchId || panel?.kind !== "employee-form") return setError("Filial não identificada para este acesso.");
    const normalizedPhone = brazilianPhoneToE164(phone);
    if (!normalizedPhone) return setError("Telefone inválido.");
    const normalizedCode = finalizeIdentifierCode(employeeCode || employeeName);
    if (normalizedCode.length < 2) return setError("Informe um código com pelo menos dois caracteres.");
    setBusy(true);
    setError("");
    try {
      const common = {
        code: normalizedCode,
        name: employeeName.trim(),
        phone: normalizedPhone,
        email: email.trim() || null,
        employmentType,
        technicalRegistration: technicalRegistration.trim() || null,
      };
      if (panel.employee) {
        await api(`/employees/${panel.employee.id}`, {
          method: "PATCH",
          headers: { "If-Match": `"${panel.employee.version}"` },
          body: JSON.stringify(common),
        });
      } else {
        await api("/employees", {
          method: "POST",
          headers: { "Idempotency-Key": newIdempotencyKey() },
          body: JSON.stringify({ ...common, branchId: user.branchId, email: common.email ?? undefined, technicalRegistration: common.technicalRegistration ?? undefined }),
        });
      }
      await Promise.all([employees.reload(), teams.reload({ background: true })]);
      closePanel();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Funcionário não salvo.");
    } finally {
      setBusy(false);
    }
  };

  const submitTeam = async (event: FormEvent) => {
    event.preventDefault();
    if (!user?.branchId || panel?.kind !== "team-form") return setError("Filial não identificada para este acesso.");
    const normalizedCode = finalizeIdentifierCode(teamCode || teamName);
    if (normalizedCode.length < 2) return setError("Informe um código com pelo menos dois caracteres.");
    setBusy(true);
    setError("");
    try {
      const common = { code: normalizedCode, name: teamName.trim(), leaderEmployeeId: leaderId || null };
      if (panel.team) {
        await api(`/teams/${panel.team.id}`, {
          method: "PATCH",
          headers: { "If-Match": `"${panel.team.version}"` },
          body: JSON.stringify(common),
        });
      } else {
        await api("/teams", {
          method: "POST",
          headers: { "Idempotency-Key": newIdempotencyKey() },
          body: JSON.stringify({ ...common, branchId: user.branchId, leaderEmployeeId: common.leaderEmployeeId ?? undefined }),
        });
      }
      await teams.reload();
      closePanel();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Equipe não salva.");
    } finally {
      setBusy(false);
    }
  };

  const submitAvailability = async (event: FormEvent) => {
    event.preventDefault();
    if (panel?.kind !== "availability") return;
    setBusy(true);
    setError("");
    try {
      await api(`/employees/${panel.employee.id}/availability`, {
        method: "POST",
        headers: { "Idempotency-Key": newIdempotencyKey() },
        body: JSON.stringify({
          kind: availabilityKind,
          startsAt: new Date(availabilityStart).toISOString(),
          endsAt: new Date(availabilityEnd).toISOString(),
          reason: availabilityReason.trim(),
        }),
      });
      closePanel();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Período não registrado.");
    } finally {
      setBusy(false);
    }
  };

  const submitMember = async (event: FormEvent) => {
    event.preventDefault();
    if (panel?.kind !== "members") return;
    setBusy(true);
    setError("");
    try {
      await api(`/teams/${panel.team.id}/members`, {
        method: "POST",
        headers: { "Idempotency-Key": newIdempotencyKey() },
        body: JSON.stringify({ employeeId: memberEmployeeId, startsAt: new Date().toISOString() }),
      });
      const addedEmployee = employees.data?.find((employee) => employee.id === memberEmployeeId);
      setMemberEmployeeId("");
      if (addedEmployee) setPanel({ kind: "members", team: { ...panel.team, members: [...panel.team.members, { id: addedEmployee.id, name: addedEmployee.name, role: "member" }] } });
      await Promise.all([teams.reload({ background: true }), employees.reload({ background: true })]);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Integrante não adicionado.");
    } finally {
      setBusy(false);
    }
  };

  const removeMember = async (team: Team, employeeId: string) => {
    setBusy(true);
    setError("");
    try {
      await api(`/teams/${team.id}/members/${employeeId}/remove`, {
        method: "POST",
        headers: { "Idempotency-Key": newIdempotencyKey() },
        body: JSON.stringify({}),
      });
      setPanel({ kind: "members", team: { ...team, members: team.members.filter((member) => member.id !== employeeId) } });
      await Promise.all([teams.reload({ background: true }), employees.reload({ background: true })]);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Integrante não removido.");
    } finally {
      setBusy(false);
    }
  };

  const removeEmployee = async (employee: Employee) => {
    setBusy(true);
    setError("");
    try {
      await api(`/employees/${employee.id}/deactivate`, {
        method: "POST",
        headers: { "Idempotency-Key": newIdempotencyKey() },
        body: JSON.stringify({ reason: "Funcionário removido pelo gestor na tela de equipe" }),
      });
      await Promise.all([employees.reload(), teams.reload({ background: true })]);
      closePanel();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Funcionário não removido.");
    } finally {
      setBusy(false);
    }
  };

  const removeTeam = async (team: Team) => {
    setBusy(true);
    setError("");
    try {
      await api(`/teams/${team.id}/deactivate`, {
        method: "POST",
        headers: { "Idempotency-Key": newIdempotencyKey() },
        body: JSON.stringify({}),
      });
      await Promise.all([teams.reload(), employees.reload({ background: true })]);
      closePanel();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Equipe não removida.");
    } finally {
      setBusy(false);
    }
  };

  const panelTitle = panel?.kind === "employee-form"
    ? panel.employee ? "Editar funcionário" : "Novo funcionário"
    : panel?.kind === "team-form"
      ? panel.team ? "Editar equipe" : "Nova equipe"
      : panel?.kind === "availability"
        ? "Indisponibilidade"
        : "Integrantes da equipe";

  return <>
    <section className="page-intro"><div className="app-container"><PageHeader
      title="Equipe"
      action={<button className="site-button site-button-navy" onClick={() => tab === "employees" ? openEmployeeForm() : openTeamForm()}><Plus size={17} /> {tab === "employees" ? "Novo funcionário" : "Nova equipe"}</button>}
    /></div></section>
    <section className="app-section compact"><div className="app-container people-page">
      <section className="people-metrics" aria-label="Resumo da equipe">
        <article><span className="people-metric-icon is-blue"><UserCheck size={19} /></span><span>Profissionais<strong>{metrics.employees}</strong></span></article>
        <article><span className="people-metric-icon is-purple"><UsersRound size={19} /></span><span>Equipes<strong>{metrics.teams}</strong></span></article>
        <article><span className="people-metric-icon is-green"><BriefcaseBusiness size={19} /></span><span>Alocados<strong>{metrics.allocated}</strong></span></article>
        <article><span className="people-metric-icon is-amber"><UserMinus size={19} /></span><span>Sem equipe<strong>{metrics.unallocated}</strong></span></article>
      </section>

      <div className="people-controls">
        <div className="filter-tabs" aria-label="Visualização da equipe"><button className={tab === "employees" ? "active" : ""} onClick={() => changeTab("employees")}>Funcionários</button><button className={tab === "teams" ? "active" : ""} onClick={() => changeTab("teams")}>Equipes</button></div>
        <label className="site-search"><Search size={18} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={tab === "employees" ? "Buscar profissional" : "Buscar equipe"} aria-label="Buscar na equipe" /></label>
      </div>

      <div className={`people-management-layout${panel ? " has-detail" : ""}`}>
        {tab === "employees" ? <StateView loading={employees.loading} error={employees.error} empty={filteredEmployees?.length === 0}>
          <div className="people-grid">{filteredEmployees?.map((employee) => <article className="people-card" key={employee.id}>
            <header className="people-card-heading"><span className="people-icon"><UserCheck size={20} /></span><div><span className="site-eyebrow">{employee.code}</span><h2>{employee.name}</h2></div><StatusBadge value={employee.active ? "active" : "inactive"} /></header>
            <div className="people-contact"><span><Phone size={15} /> {displayPhone(employee.phone)}</span>{employee.email && <span><Mail size={15} /> {employee.email}</span>}</div>
            <div className="people-card-facts"><span>Vínculo<strong>{employmentLabels[employee.employmentType]}</strong></span><span>Equipe<strong>{employee.teams.length ? employee.teams.map(({ name }) => name).join(" · ") : "Não alocado"}</strong></span></div>
            <footer><button type="button" onClick={() => openEmployeeForm(employee)}><Pencil size={15} /> Editar</button><button type="button" onClick={() => openAvailability(employee)}><CalendarOff size={15} /> Indisponibilidade</button></footer>
          </article>)}</div>
        </StateView> : <StateView loading={teams.loading} error={teams.error} empty={filteredTeams?.length === 0}>
          <div className="people-grid">{filteredTeams?.map((team) => <article className="people-card team-card" key={team.id}>
            <header className="people-card-heading"><span className="people-icon is-team"><UsersRound size={20} /></span><div><span className="site-eyebrow">{team.code}</span><h2>{team.name}</h2></div><StatusBadge value={team.active ? "active" : "inactive"} /></header>
            <div className="people-card-facts"><span>Responsável<strong>{team.leaderName ?? "Não definido"}</strong></span><span>Integrantes<strong>{team.members.length}</strong></span></div>
            {team.members.length > 0 && <div className="people-member-preview">{team.members.slice(0, 3).map((member) => <span key={member.id}>{member.name}</span>)}{team.members.length > 3 && <span>+{team.members.length - 3}</span>}</div>}
            <footer><button type="button" onClick={() => openTeamForm(team)}><Pencil size={15} /> Editar</button><button type="button" onClick={() => { setMemberEmployeeId(""); setError(""); setPanel({ kind: "members", team }); }}><UserPlus size={15} /> Integrantes</button></footer>
          </article>)}</div>
        </StateView>}

        {panel && <ResponsiveDetailSurface title={panelTitle} onClose={closePanel} className="people-detail-surface">
          {panel.kind === "employee-form" && <form className="workspace-card people-editor" onSubmit={submitEmployee}>
            <header><span className="people-icon"><UserCheck size={20} /></span><div><h2>{panel.employee?.name ?? "Novo funcionário"}</h2></div></header>
            <div className="people-editor-grid">
              <label className="is-wide">Nome<input value={employeeName} onChange={(event) => setEmployeeName(event.target.value)} required minLength={2} autoFocus /></label>
              <label>Código<input value={employeeCode} onChange={(event) => setEmployeeCode(formatIdentifierCode(event.target.value))} onBlur={() => setEmployeeCode(finalizeIdentifierCode(employeeCode || employeeName))} placeholder="Ex.: JOAO_01" /></label>
              <label>Vínculo<select value={employmentType} onChange={(event) => setEmploymentType(event.target.value as Employee["employmentType"])}><option value="employee">Funcionário</option><option value="contractor">Terceirizado</option><option value="partner">Sócio</option></select></label>
              <label>Telefone<input value={phone} onChange={(event) => setPhone(formatBrazilianPhone(event.target.value))} required inputMode="tel" autoComplete="tel" placeholder="(21) 99999-9999" /></label>
              <label>E-mail<input value={email} onChange={(event) => setEmail(formatEmailInput(event.target.value))} type="email" autoComplete="email" placeholder="nome@empresa.com" /></label>
              <label className="is-wide">Registro técnico<input value={technicalRegistration} onChange={(event) => setTechnicalRegistration(event.target.value)} placeholder="Opcional" /></label>
            </div>
            {error && <div className="form-error" role="alert">{error}</div>}
            <div className="people-editor-actions"><button type="button" className="site-button site-button-outline" onClick={closePanel}>Cancelar</button><button className="site-button site-button-navy" disabled={busy}><Save size={16} /> {panel.employee ? "Salvar alterações" : "Cadastrar funcionário"}</button></div>
            {panel.employee && <div className="people-remove-area">{confirmingRemoval ? <div className="people-remove-confirm"><span><strong>Remover funcionário?</strong><small>Ele sairá das equipes e perderá o acesso à plataforma.</small></span><div><button type="button" onClick={() => setConfirmingRemoval(false)} disabled={busy}>Voltar</button><button type="button" className="danger" onClick={() => void removeEmployee(panel.employee!)} disabled={busy}>Remover</button></div></div> : <button type="button" className="people-remove-trigger" onClick={() => setConfirmingRemoval(true)}><Trash2 size={15} /> Remover funcionário</button>}</div>}
          </form>}

          {panel.kind === "team-form" && <form className="workspace-card people-editor" onSubmit={submitTeam}>
            <header><span className="people-icon is-team"><UsersRound size={20} /></span><div><span>{panel.team ? "Dados da equipe" : "Cadastro"}</span><h2>{panel.team?.name ?? "Nova equipe"}</h2></div></header>
            <div className="people-editor-grid">
              <label className="is-wide">Nome<input value={teamName} onChange={(event) => setTeamName(event.target.value)} required minLength={2} autoFocus /></label>
              <label>Código<input value={teamCode} onChange={(event) => setTeamCode(formatIdentifierCode(event.target.value))} onBlur={() => setTeamCode(finalizeIdentifierCode(teamCode || teamName))} required minLength={2} /></label>
              <label>Responsável<select value={leaderId} onChange={(event) => setLeaderId(event.target.value)}><option value="">Sem responsável</option>{employees.data?.map((employee) => <option key={employee.id} value={employee.id}>{employee.name}</option>)}</select></label>
            </div>
            {error && <div className="form-error" role="alert">{error}</div>}
            <div className="people-editor-actions"><button type="button" className="site-button site-button-outline" onClick={closePanel}>Cancelar</button><button className="site-button site-button-navy" disabled={busy}><Save size={16} /> {panel.team ? "Salvar alterações" : "Criar equipe"}</button></div>
            {panel.team && <div className="people-remove-area">{confirmingRemoval ? <div className="people-remove-confirm"><span><strong>Remover equipe?</strong><small>Os integrantes continuarão ativos e os vínculos com esta equipe serão encerrados.</small></span><div><button type="button" onClick={() => setConfirmingRemoval(false)} disabled={busy}>Voltar</button><button type="button" className="danger" onClick={() => void removeTeam(panel.team!)} disabled={busy}>Remover</button></div></div> : <button type="button" className="people-remove-trigger" onClick={() => setConfirmingRemoval(true)}><Trash2 size={15} /> Remover equipe</button>}</div>}
          </form>}

          {panel.kind === "availability" && <form className="workspace-card people-editor" onSubmit={submitAvailability}>
            <header><span className="people-icon is-away"><CalendarOff size={20} /></span><div><span>Registrar período</span><h2>{panel.employee.name}</h2></div></header>
            <div className="people-editor-grid">
              <label className="is-wide">Tipo<select value={availabilityKind} onChange={(event) => setAvailabilityKind(event.target.value as typeof availabilityKind)}><option value="unavailable">Indisponível</option><option value="leave">Folga ou afastamento</option><option value="break">Intervalo</option></select></label>
              <label>Início<input type="datetime-local" value={availabilityStart} onChange={(event) => setAvailabilityStart(event.target.value)} required /></label>
              <label>Fim<input type="datetime-local" value={availabilityEnd} onChange={(event) => setAvailabilityEnd(event.target.value)} required /></label>
              <label className="is-wide">Motivo<input value={availabilityReason} onChange={(event) => setAvailabilityReason(event.target.value)} required minLength={3} /></label>
            </div>
            {error && <div className="form-error" role="alert">{error}</div>}
            <div className="people-editor-actions"><button type="button" className="site-button site-button-outline" onClick={closePanel}>Cancelar</button><button className="site-button site-button-navy" disabled={busy}><CalendarOff size={16} /> Registrar período</button></div>
          </form>}

          {panel.kind === "members" && <section className="workspace-card people-editor people-members-panel">
            <div className="people-members-list">{panel.team.members.length ? panel.team.members.map((member) => <div key={member.id}><span className="people-member-avatar">{member.name.slice(0, 1).toUpperCase()}</span><span><strong>{member.name}</strong><small>{member.role === "member" ? "Integrante" : member.role}</small></span><button type="button" onClick={() => void removeMember(panel.team, member.id)} disabled={busy} aria-label={`Remover ${member.name}`}><Trash2 size={15} /></button></div>) : <p>Nenhum integrante nesta equipe.</p>}</div>
            <form className="people-add-member" onSubmit={submitMember}><label>Adicionar profissional<select value={memberEmployeeId} onChange={(event) => setMemberEmployeeId(event.target.value)} required><option value="">Selecione</option>{employees.data?.filter((employee) => !panel.team.members.some((member) => member.id === employee.id)).map((employee) => <option key={employee.id} value={employee.id}>{employee.name}</option>)}</select></label><button className="site-button site-button-navy" disabled={busy || !memberEmployeeId}><UserPlus size={16} /> Adicionar</button></form>
            {error && <div className="form-error" role="alert">{error}</div>}
          </section>}
        </ResponsiveDetailSurface>}
      </div>
    </div></section>
  </>;
}
